import React, { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import ClosedCaptionOutlinedIcon from "@mui/icons-material/ClosedCaptionOutlined";
import Hls from "hls.js";

import type {
    CaptionsDisplayMode,
    CaptionsTextStyle,
    CaptionsHighlightStyle,
    CaptionsBackground,
    CaptionsPhraseConfig,
    CaptionsTextCase,
    CaptionsTextAlign,
    CaptionWord,
} from "../../engine/clips/captions/captions.types";
import { CaptionsClip } from "../../engine/clips/captions";
import {
    DEFAULT_BACKGROUND,
    DEFAULT_HIGHLIGHT_STYLE,
    DEFAULT_PHRASE_CONFIG,
    DEFAULT_TEXT_STYLE,
} from "../../engine/clips/captions/captions.interfaces";
import { CompositionService } from "../../librettoEditor/services/CompositionService";
import { CompositionStateStore } from "../../librettoEditor/store/CompositionStateStore";
import { ScriptLayerStore } from "../../librettoEditor/store/ScriptLayerStore";
import { ApplicationStore } from "../../librettoEditor/store/ApplicationStore";
import { Timestamp } from "../../engine/models";
import { useAssetStore } from "../../storage/AssetStore";
import { ProjectAssetsStore } from "../../state/ProjectAssetsStore";

// ============ Preset Type Definition ============

export interface CaptionPresetConfig {
    id: string;
    name: string;
    displayMode: CaptionsDisplayMode;
    textCase?: CaptionsTextCase;
    textAlign?: CaptionsTextAlign;
    maxWidth?: number | string;
    textStyle: Partial<CaptionsTextStyle>;
    highlightStyle?: Partial<CaptionsHighlightStyle>;
    background?: Partial<CaptionsBackground>;
    phraseConfig?: Partial<CaptionsPhraseConfig>;
    // Preview-specific (for rendering the card)
    previewText?: string;
    previewHighlightIndex?: number; // which word to highlight in preview
    previewVideoUrl?: string; // m3u8 URL for video preview
}

const BASE_CAPTION_CANVAS_HEIGHT = 1080;
const MIN_CAPTION_SCALE = 1;
const MAX_CAPTION_SCALE = 1.55;
const CAPTION_OVERLAP_EPS_SEC = 0.02;

type CaptionApplyScope = 'all' | 'scene';

type CaptionsTarget = {
    clipStart: Timestamp;
    clipStop: Timestamp;
    scriptRange: { startSec: number; endSec: number } | null;
};

type PendingCaptionPresetAction = {
    preset: CaptionPresetConfig;
    existingClips: CaptionsClip[];
    scope: CaptionApplyScope;
};

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const getCaptionScaleForComposition = (composition: any): number => {
    const compositionHeight = Number(composition?.height ?? BASE_CAPTION_CANVAS_HEIGHT);
    if (!Number.isFinite(compositionHeight) || compositionHeight <= 0) {
        return 1;
    }
    return clamp(compositionHeight / BASE_CAPTION_CANVAS_HEIGHT, MIN_CAPTION_SCALE, MAX_CAPTION_SCALE);
};

const scaleCaptionTextStyle = (
    textStyle: Partial<CaptionsTextStyle> | undefined,
    scale: number,
): Partial<CaptionsTextStyle> | undefined => {
    if (!textStyle) return textStyle;
    const next: Partial<CaptionsTextStyle> = { ...textStyle };

    if (typeof next.fontSize === "number") {
        next.fontSize = Math.max(30, Math.round(next.fontSize * scale));
    }

    if (next.stroke) {
        next.stroke = {
            ...next.stroke,
            width: Math.max(1, Math.round(Number(next.stroke.width ?? 1) * scale)),
        };
    }

    if (next.shadow) {
        next.shadow = {
            ...next.shadow,
            blur: Math.max(0, Number(next.shadow.blur ?? 0) * scale),
            distance: Number(next.shadow.distance ?? 0) * scale,
        };
    }

    return next;
};

const scaleCaptionHighlightStyle = (
    highlightStyle: Partial<CaptionsHighlightStyle> | undefined,
    scale: number,
): Partial<CaptionsHighlightStyle> | undefined => {
    if (!highlightStyle) return highlightStyle;
    const next: Partial<CaptionsHighlightStyle> = { ...highlightStyle };
    if (next.stroke) {
        next.stroke = {
            ...next.stroke,
            width: Math.max(1, Math.round(Number(next.stroke.width ?? 1) * scale)),
        };
    }
    if (next.background) {
        next.background = {
            ...next.background,
            paddingX: Number.isFinite(Number(next.background.paddingX))
                ? Math.max(0, Math.round(Number(next.background.paddingX) * scale))
                : next.background.paddingX,
            paddingY: Number.isFinite(Number(next.background.paddingY))
                ? Math.max(0, Math.round(Number(next.background.paddingY) * scale))
                : next.background.paddingY,
            borderRadius: Number.isFinite(Number(next.background.borderRadius))
                ? Math.max(0, Math.round(Number(next.background.borderRadius) * scale))
                : next.background.borderRadius,
        };
    }
    return next;
};

const scaleCaptionBackground = (
    background: Partial<CaptionsBackground> | undefined,
    scale: number,
): Partial<CaptionsBackground> | undefined => {
    if (!background) return background;
    const next: Partial<CaptionsBackground> = { ...background };

    if (typeof next.borderRadius === "number") {
        next.borderRadius = Math.max(0, Math.round(next.borderRadius * scale));
    }

    if (next.padding) {
        next.padding = {
            x: Math.max(0, Math.round(Number(next.padding.x ?? 0) * scale)),
            y: Math.max(0, Math.round(Number(next.padding.y ?? 0) * scale)),
        };
    }

    return next;
};

const resolveCaptionsTarget = (scriptClip: any, applyScope: CaptionApplyScope): CaptionsTarget => {
    let clipStart = scriptClip.start;
    let clipStop = scriptClip.stop;
    let scriptRange: { startSec: number; endSec: number } | null = null;

    if (applyScope === "scene") {
        const scenes: any[] = Array.isArray(scriptClip.getScenes?.()) ? scriptClip.getScenes() : [];
        if (scenes.length > 0) {
            const idxExplicit = ApplicationStore.selectedSceneIndex;
            let sceneIndex: number | null = idxExplicit != null ? idxExplicit : null;
            if (sceneIndex == null) {
                const srcTime = ScriptLayerStore.mapTimelineTimeToSourceTime(Number(CompositionStateStore.currentTime ?? 0));
                if (srcTime != null && typeof scriptClip.findSceneIndexForTime === "function") {
                    sceneIndex = scriptClip.findSceneIndexForTime(srcTime);
                }
            }
            if (sceneIndex != null) {
                const scene = scenes.find((s: any) => Number(s?.index) === Number(sceneIndex)) ?? scenes[sceneIndex];
                const startSec = Number(scene?.startSec ?? 0);
                const endSec = Number(scene?.endSec ?? startSec);
                if (Number.isFinite(startSec) && Number.isFinite(endSec) && endSec > startSec) {
                    scriptRange = { startSec, endSec };
                    const startTimeline = ScriptLayerStore.mapSourceTimeToCompositionTime(startSec) ?? 0;
                    const endTimeline =
                        ScriptLayerStore.mapSourceTimeToCompositionTime(endSec) ??
                        Number(scriptClip.timelineDuration?.seconds ?? endSec);
                    const base = Number(scriptClip.start?.seconds ?? 0);
                    clipStart = Timestamp.fromSeconds(base + Math.max(0, startTimeline));
                    clipStop = Timestamp.fromSeconds(base + Math.max(0, endTimeline));
                }
            }
        }
    }

    return { clipStart, clipStop, scriptRange };
};

const findOverlappingCaptionsClips = (
    composition: any,
    scriptClip: any,
    clipStart: Timestamp,
    clipStop: Timestamp,
): CaptionsClip[] => {
    const targetStartSec = Number(clipStart?.seconds ?? 0);
    const targetStopSec = Number(clipStop?.seconds ?? targetStartSec);
    if (!Number.isFinite(targetStartSec) || !Number.isFinite(targetStopSec) || targetStopSec <= targetStartSec) {
        return [];
    }

    const scriptClipId = String(scriptClip?.id ?? "");
    const existing: CaptionsClip[] = [];
    const tracks: any[] = Array.isArray(composition?.tracks) ? composition.tracks : [];
    for (const track of tracks) {
        const clips: any[] = Array.isArray(track?.clips) ? track.clips : [];
        for (const clip of clips) {
            if (String(clip?.type ?? "") !== "captions") continue;
            const clipScriptId = String(clip?.scriptClipId ?? "");
            if (clipScriptId && scriptClipId && clipScriptId !== scriptClipId) continue;

            const existingStartSec = Number(clip?.start?.seconds ?? 0);
            const existingStopSec = Number(clip?.stop?.seconds ?? existingStartSec);
            if (!Number.isFinite(existingStartSec) || !Number.isFinite(existingStopSec)) continue;
            if (existingStartSec < targetStopSec - CAPTION_OVERLAP_EPS_SEC && existingStopSec > targetStartSec + CAPTION_OVERLAP_EPS_SEC) {
                existing.push(clip as CaptionsClip);
            }
        }
    }
    return existing;
};

const applyPresetToCaptionsClip = (captionsClip: CaptionsClip, preset: CaptionPresetConfig, composition: any): void => {
    const captionScale = getCaptionScaleForComposition(composition);
    const scaledTextStyle = scaleCaptionTextStyle(preset.textStyle, captionScale);
    const scaledHighlightStyle = scaleCaptionHighlightStyle(preset.highlightStyle, captionScale);
    const scaledBackground = scaleCaptionBackground(preset.background, captionScale);

    // Reset preset-managed fields first so replacing a preset does not carry stale merged style.
    (captionsClip as any)._textStyle = { ...DEFAULT_TEXT_STYLE };
    (captionsClip as any)._highlightStyle = { ...DEFAULT_HIGHLIGHT_STYLE };
    (captionsClip as any)._background = { ...DEFAULT_BACKGROUND };
    (captionsClip as any)._phraseConfig = { ...DEFAULT_PHRASE_CONFIG };
    (captionsClip as any)._sceneStyleOverrides = {};

    captionsClip.set({
        displayMode: preset.displayMode,
        textCase: preset.textCase ?? "none",
        textAlign: preset.textAlign ?? "center",
        maxWidth: preset.maxWidth ?? "80%",
        textStyle: scaledTextStyle as any,
        highlightStyle: scaledHighlightStyle,
        background: scaledBackground,
        phraseConfig: preset.phraseConfig,
    });
};

// ============ Caption Presets ============

export const CAPTION_PRESETS: CaptionPresetConfig[] = [
    {
        id: 'typewriter',
        name: 'Typewriter',
        displayMode: 'typewriter',
        maxWidth: '76%',
        textStyle: {
            fontFamily: 'Courier New',
            fontSize: 38,
            fontWeight: 'normal',
            fillColor: '#FFFFFF',
            stroke: { color: '#000000', width: 5, join: 'round' },
            shadow: null,
        },
        highlightStyle: {
            color: '#FFFFFF',
            stroke: { color: '#000000', width: 5, join: 'round' },
        },
        background: {
            fill: '#000000',
            alpha: 0,
        },
        phraseConfig: {
            maxWordsPerPhrase: 8,
            maxPhraseDuration: 3.2,
            maxPhraseLength: 52,
        },
        previewText: 'This is a caption',
        previewHighlightIndex: -1,
        previewVideoUrl: 'https://stream.mux.com/00Ko2HI001gVUNay3sKeFl9HjCL2IGLJ01mD7j7hOY701Ls.m3u8', // TODO: Add m3u8 URL
    },
    {
        id: 'karaoke-classic',
        name: 'Karaoke classic',
        displayMode: 'phrase',
        textStyle: {
            fontFamily: 'Arial',
            fontSize: 38,
            fontWeight: 'bold',
            fillColor: '#FFFFFF',
            stroke: { color: '#000000', width: 5, join: 'round' },
            shadow: null,
        },
        highlightStyle: {
            color: '#FFFFFF',
            background: {
                fill: '#3B82F6',
                alpha: 1,
                paddingX: 10,
                paddingY: 6,
                borderRadius: 8,
            },
        },
        background: {
            fill: '#000000',
            alpha: 0,
        },
        phraseConfig: {
            maxWordsPerPhrase: 8,
        },
        previewText: 'This is a classic karaoke highlighted caption',
        previewHighlightIndex: 5,
        previewVideoUrl: 'https://stream.mux.com/9pkIG2foH3oi8BjWeR3rMtLc8i4dhDzOEtD5YLItHkE.m3u8', // TODO: Add m3u8 URL
    },
    {
        id: 'bold-italic-green',
        name: 'Bold Italic: Green',
        displayMode: 'phrase',
        textStyle: {
            fontFamily: 'Georgia',
            fontSize: 38,
            fontWeight: 'bold',
            fontStyle: 'italic',
            fillColor: '#FFFFFF',
            stroke: { color: '#000000', width: 6, join: 'round' },
            shadow: null,
        },
        highlightStyle: {
            color: '#22C55E',
        },
        background: {
            fill: '#000000',
            alpha: 0,
        },
        previewText: 'italicized white text, a heavy',
        previewHighlightIndex: 2,
        previewVideoUrl: 'https://stream.mux.com/hZmlzOJGmSrcJpavLtlzNFyuY02UQ00b02GzygAfV3j7PE.m3u8', // TODO: Add m3u8 URL
    },
    {
        id: 'impact-yellow',
        name: 'Impact: Yellow',
        displayMode: 'phrase',
        textCase: 'upper',
        maxWidth: '74%',
        textStyle: {
            fontFamily: 'Impact',
            fontSize: 38,
            fontWeight: 'bold',
            fillColor: '#FFFFFF',
            stroke: { color: '#000000', width: 7, join: 'round' },
            shadow: null,
        },
        highlightStyle: {
            color: '#FACC15',
        },
        background: {
            fill: '#000000',
            alpha: 0,
        },
        phraseConfig: {
            maxWordsPerPhrase: 6,
            maxPhraseDuration: 3.2,
            maxPhraseLength: 44,
        },
        previewText: 'NARROW, BOLD TEXT, A HEAVY BLACK',
        previewHighlightIndex: -1,
        previewVideoUrl: 'https://stream.mux.com/KuEOIFHjlhTUKI3XADg402L7U3OOn6QMr2O02dXv22SYs.m3u8', // TODO: Add m3u8 URL
    },
    // {
    //     id: 'modern-yellow-waveform',
    //     name: 'Modern yellow waveform',
    //     displayMode: 'word',
    //     textStyle: {
    //         fontFamily: 'Helvetica Neue',
    //         fontSize: 52,
    //         fontWeight: 'bold',
    //         fillColor: '#FACC15',
    //         stroke: { color: '#000000', width: 4, join: 'round' },
    //         shadow: null,
    //     },
    //     background: {
    //         fill: '#000000',
    //         alpha: 0,
    //     },
    //     previewText: 'outline and a waveform',
    //     previewHighlightIndex: -1,
    //     previewVideoUrl: 'https://stream.mux.com/oMjB01nxGXM9s02lMcAfIQLqQBdx9zd88E3RF02XyLT8zw.m3u8', // TODO: Add m3u8 URL
    // },
    {
        id: 'classic',
        name: 'Classic',
        displayMode: 'phrase',
        maxWidth: '66%',
        textStyle: {
            fontFamily: 'Arial',
            fontSize: 38,
            fontWeight: 'normal',
            fillColor: '#FFFFFF',
            stroke: null,
            shadow: null,
        },
        background: {
            fill: '#000000',
            alpha: 0,
        },
        highlightStyle: {
            color: '#FFFFFF',
        },
        phraseConfig: {
            maxWordsPerPhrase: 14,
            maxPhraseDuration: 4.4,
            maxPhraseLength: 60,
        },
        previewText: 'This is a clean caption with white text and no border that takes up',
        previewHighlightIndex: -1,
        previewVideoUrl: 'https://stream.mux.com/wnvhT5zZSaTw4eNXjizxVlCwaFH7LGcNaQ7UHe2gBwc.m3u8', // TODO: Add m3u8 URL
    },
    {
        id: 'bold-two-words',
        name: 'Bold: Two words',
        displayMode: 'phrase',
        textCase: 'upper',
        maxWidth: '68%',
        textStyle: {
            fontFamily: 'Arial Black',
            fontSize: 38,
            fontWeight: 'bold',
            fillColor: '#FFFFFF',
            stroke: null,
            shadow: { color: '#000000', blur: 16, distance: 0, angle: Math.PI / 4, alpha: 0.55 },
        },
        highlightStyle: {
            color: '#FFFFFF',
        },
        background: {
            fill: '#000000',
            alpha: 0,
        },
        phraseConfig: {
            maxWordsPerPhrase: 2,
        },
        previewText: 'STYLE WITH',
        previewHighlightIndex: -1,
        previewVideoUrl: 'https://stream.mux.com/BQvTX2R2PPPHzDE8nORdN1JsoYt9hMEdaBDSF02fXZxk.m3u8', // TODO: Add m3u8 URL
    },
    {
        id: 'bold-yellow-highlight',
        name: 'Bold: Yellow highlight',
        displayMode: 'phrase',
        textCase: 'upper',
        maxWidth: '72%',
        textStyle: {
            fontFamily: 'Arial Black',
            fontSize: 38,
            fontWeight: 'bold',
            fillColor: '#FFFFFF',
            stroke: null,
            shadow: { color: '#000000', blur: 16, distance: 0, angle: Math.PI / 4, alpha: 0.55 },
        },
        highlightStyle: {
            color: '#FACC15',
        },
        background: {
            fill: '#000000',
            alpha: 0,
        },
        previewText: 'THIS IS A LARGE BOLD WHITE CAPTION STYLE',
        previewHighlightIndex: 7,
        previewVideoUrl: 'https://stream.mux.com/pso0138gmsuYn8Up1KcwIW8ZGCYkPdK4YNiYm4LwLhRQ.m3u8', // TODO: Add m3u8 URL
    },
    {
        id: 'clean-paragraph',
        name: 'Clean:paragraph',
        displayMode: 'phrase',
        maxWidth: '70%',
        textStyle: {
            fontFamily: 'Helvetica',
            fontSize: 38,
            fontWeight: 'normal',
            fillColor: '#FFFFFF',
            stroke: null,
            shadow: null,
        },
        background: {
            fill: '#000000',
            alpha: 0,
        },
        highlightStyle: {
            color: '#FFFFFF',
        },
        phraseConfig: {
            maxWordsPerPhrase: 20,
            maxPhraseDuration: 5.5,
            maxPhraseLength: 140,
        },
        previewText: 'This is a clean and simple multiline',
        previewHighlightIndex: -1,
        previewVideoUrl: 'https://stream.mux.com/xICLL3jdaUblnnesCGu8P00P4qDC01AkJFF3xnj00RqyGE.m3u8', // TODO: Add m3u8 URL
    },
];

// ============ Styles ============

const titleTextStyle: React.CSSProperties = {
    color: "var(--app-text-primary)",
    paddingLeft: "8px",
    paddingRight: "8px",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    fontFamily: "Inter, sans-serif",
    userSelect: "none",
};

const toPreviewRgba = (color: string, alpha: number): string => {
    const safeAlpha = Number.isFinite(alpha) ? Math.min(1, Math.max(0, alpha)) : 1;
    if (!color || safeAlpha >= 1) return color;
    if (!color.startsWith('#')) return color;
    const hex = color.slice(1);
    const normalized = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    if (normalized.length !== 6) return color;
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
};

const getPreviewBackgroundStyle = (background?: Partial<CaptionsBackground>): React.CSSProperties => {
    const alpha = Number(background?.alpha ?? 0);
    if (!(alpha > 0)) return {};
    const paddingX = Math.max(4, Math.round(Number(background?.padding?.x ?? 12) * 0.35));
    const paddingY = Math.max(2, Math.round(Number(background?.padding?.y ?? 6) * 0.35));
    const radius = Math.max(3, Math.round(Number(background?.borderRadius ?? 6) * 0.5));
    return {
        backgroundColor: toPreviewRgba(String(background?.fill ?? '#000000'), alpha),
        padding: `${paddingY}px ${paddingX}px`,
        borderRadius: `${radius}px`,
    };
};

const getPreviewTextShadow = (shadow?: CaptionsTextStyle["shadow"]): string | undefined => {
    if (!shadow) return undefined;
    const scale = 0.35;
    const distance = Number(shadow.distance ?? 0) * scale;
    const blur = Number(shadow.blur ?? 0) * scale;
    const angle = Number(shadow.angle ?? 0);
    const offsetX = Math.round(Math.cos(angle) * distance);
    const offsetY = Math.round(Math.sin(angle) * distance);
    const color = toPreviewRgba(String(shadow.color ?? '#000000'), Number(shadow.alpha ?? 1));
    return `${offsetX}px ${offsetY}px ${Math.max(0, Math.round(blur))}px ${color}`;
};

const getPreviewTextStroke = (stroke?: CaptionsTextStyle["stroke"]): string | undefined => {
    if (!stroke) return undefined;
    const scale = 0.35;
    const width = Math.max(1, Math.round(Number(stroke.width ?? 1) * scale));
    return `${width}px ${stroke.color}`;
};

// ============ Components ============

const PaperPanel: React.FC<{
    title: string;
    children: React.ReactNode;
    fill?: boolean;
    headerContent?: React.ReactNode;
}> = ({ title, children, fill, headerContent }) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            background: "var(--app-bg-surface)",
            border: "0.5px solid var(--app-border-subtle)",
            borderRadius: "12px",
            px: 1.5,
            py: 1.5,
            ...(fill ? { flex: 1, minHeight: 0 } : {}),
        }}
    >
        {headerContent && (
            <Box sx={{ mb: 1.5 }}>
                {headerContent}
            </Box>
        )}
        <Box sx={{ ...(fill ? { overflow: "auto" } : {}) }}>{children}</Box>
    </Box>
);

/**
 * HLS Video Player component for preset previews
 */
const PreviewVideoPlayer: React.FC<{
    src: string;
    onError?: () => void;
}> = ({ src, onError }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !src) return;

        // Check if HLS is supported
        if (Hls.isSupported()) {
            const hls = new Hls({
                enableWorker: false,
                lowLatencyMode: false,
            });
            hlsRef.current = hls;

            hls.loadSource(src);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                video.muted = true;
                video.play().catch(() => {});
            });

            hls.on(Hls.Events.ERROR, (_, data) => {
                if (data.fatal) {
                    console.warn('[PreviewVideoPlayer] HLS fatal error:', data.type);
                    onError?.();
                }
            });

            return () => {
                hls.destroy();
                hlsRef.current = null;
            };
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Native HLS support (Safari)
            video.src = src;
            video.muted = true;
            video.play().catch(() => {});

            return () => {
                video.src = '';
            };
        } else {
            console.warn('[PreviewVideoPlayer] HLS not supported');
            onError?.();
        }
    }, [src, onError]);

    return (
        <video
            ref={videoRef}
            muted
            loop
            playsInline
            style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center bottom', // Show bottom of video where captions appear
            }}
        />
    );
};

/**
 * Preview card for a caption preset
 */
const PresetCard: React.FC<{
    preset: CaptionPresetConfig;
    onClick: (preset: CaptionPresetConfig) => void;
}> = ({ preset, onClick }) => {
    const [videoError, setVideoError] = useState(false);
    const hasVideo = preset.previewVideoUrl && preset.previewVideoUrl.length > 0;
    const showVideo = hasVideo && !videoError;

    const words = (preset.previewText || '').split(' ');
    const highlightIdx = preset.previewHighlightIndex ?? -1;
    const previewBackgroundStyle = getPreviewBackgroundStyle(preset.background);
    const hasPreviewBackground = Boolean(previewBackgroundStyle.backgroundColor);
    const previewTextShadow = getPreviewTextShadow(preset.textStyle.shadow);
    const previewTextStroke = getPreviewTextStroke(preset.textStyle.stroke);

    // Render preview text with optional highlighting (fallback when no video)
    const renderPreviewText = () => {
        if (preset.displayMode === 'word') {
            // Single word mode - show one word
            const word = words[0] || 'Word';
            
            return (
                <Box
                    sx={{
                        display: 'inline-block',
                        fontFamily: preset.textStyle.fontFamily || 'Arial',
                        fontSize: '18px',
                        fontWeight: preset.textStyle.fontWeight || 'bold',
                        fontStyle: preset.textStyle.fontStyle || 'normal',
                        color: preset.textStyle.fillColor || '#FFFFFF',
                        textShadow: !hasPreviewBackground ? (previewTextShadow || 'none') : 'none',
                        WebkitTextStroke: previewTextStroke,
                        ...previewBackgroundStyle,
                    }}
                >
                    {word}
                </Box>
            );
        }

        // Phrase mode - show words with optional highlight
        return (
            <Box
                sx={{
                    display: 'inline-block',
                    fontFamily: preset.textStyle.fontFamily || 'Arial',
                    fontSize: '14px',
                    fontWeight: preset.textStyle.fontWeight || 'bold',
                    fontStyle: preset.textStyle.fontStyle || 'normal',
                    lineHeight: 1.4,
                    textAlign: 'center',
                    textShadow: !hasPreviewBackground ? (previewTextShadow || 'none') : 'none',
                    WebkitTextStroke: previewTextStroke,
                    ...previewBackgroundStyle,
                }}
            >
                {words.map((word, idx) => (
                    <span
                        key={idx}
                        style={{
                            color: idx === highlightIdx
                                ? (preset.highlightStyle?.color || '#FFD700')
                                : (preset.textStyle.fillColor || '#FFFFFF'),
                            backgroundColor:
                                idx === highlightIdx && preset.highlightStyle?.background?.fill
                                    ? toPreviewRgba(
                                        String(preset.highlightStyle.background.fill),
                                        Number(preset.highlightStyle.background.alpha ?? 1),
                                    )
                                    : 'transparent',
                            borderRadius:
                                idx === highlightIdx && preset.highlightStyle?.background?.fill
                                    ? `${Math.max(4, Math.round(Number(preset.highlightStyle.background.borderRadius ?? 6) * 0.5))}px`
                                    : undefined,
                            padding:
                                idx === highlightIdx && preset.highlightStyle?.background?.fill
                                    ? `${Math.max(1, Math.round(Number(preset.highlightStyle.background.paddingY ?? 4) * 0.35))}px ${Math.max(2, Math.round(Number(preset.highlightStyle.background.paddingX ?? 6) * 0.35))}px`
                                    : undefined,
                            marginRight: idx < words.length - 1 ? '0.3em' : 0,
                        }}
                    >
                        {word}
                    </span>
                ))}
            </Box>
        );
    };

    return (
        <Box
            onClick={() => onClick(preset)}
            sx={{
                cursor: 'pointer',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid var(--app-border-subtle)',
                transition: 'all 0.15s ease',
                '&:hover': {
                    borderColor: '#3B82F6',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
                },
            }}
        >
            {/* Preview area */}
            <Box
                sx={{
                    backgroundColor: '#374151',
                    height: '56px', // Compact height - shows bottom of video where captions are
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {showVideo ? (
                    <PreviewVideoPlayer
                        src={preset.previewVideoUrl!}
                        onError={() => setVideoError(true)}
                    />
                ) : (
                    <Box sx={{ px: 2, py: 2 }}>
                        {renderPreviewText()}
                    </Box>
                )}
            </Box>

            {/* Label */}
            <Box
                sx={{
                    backgroundColor: 'var(--app-bg-surface)',
                    py: 1,
                    px: 1.5,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '13px',
                        fontWeight: 400,
                        color: 'var(--app-text-muted)',
                        textAlign: 'center',
                    }}
                >
                    {preset.name}
                </Typography>
            </Box>
        </Box>
    );
};

// ============ Scope Toggle Component ============

const ScopeToggle: React.FC<{
    value: 'all' | 'scene';
    onChange: (value: 'all' | 'scene') => void;
}> = ({ value, onChange }) => {
    return (
        <Box
            sx={{
                display: 'inline-flex',
                borderRadius: '8px',
                backgroundColor: 'var(--app-bg-subtle)',
                padding: '3px',
                gap: '2px',
            }}
        >
            <Box
                onClick={() => onChange('all')}
                sx={{
                    px: 2,
                    py: 0.75,
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    backgroundColor: value === 'all' ? 'var(--app-bg-surface)' : 'transparent',
                    color: value === 'all' ? 'var(--app-text-primary)' : 'var(--app-text-muted)',
                    boxShadow: value === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    '&:hover': {
                        color: 'var(--app-text-primary)',
                    },
                }}
            >
                All scenes
            </Box>
            <Box
                onClick={() => onChange('scene')}
                sx={{
                    px: 2,
                    py: 0.75,
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    backgroundColor: value === 'scene' ? 'var(--app-bg-surface)' : 'transparent',
                    color: value === 'scene' ? 'var(--app-text-primary)' : 'var(--app-text-muted)',
                    boxShadow: value === 'scene' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    '&:hover': {
                        color: 'var(--app-text-primary)',
                    },
                }}
            >
                This scene
            </Box>
        </Box>
    );
};

// ============ Main Component ============

interface V2CaptionsSidebarProps {
    onSelectPreset?: (preset: CaptionPresetConfig) => void;
}

const V2CaptionsSidebar: React.FC<V2CaptionsSidebarProps> = ({ onSelectPreset }) => {
    const [applyScope, setApplyScope] = useState<CaptionApplyScope>('all');
    const [isCreating, setIsCreating] = useState(false);
    const [pendingPresetAction, setPendingPresetAction] = useState<PendingCaptionPresetAction | null>(null);
    const { GetTranscript } = useAssetStore();

    const createCaptionsClip = async (preset: CaptionPresetConfig, opts?: { skipExistingCheck?: boolean }) => {
        console.log('[V2CaptionsSidebar] Selected preset:', preset.name);
        
        if (isCreating) {
            console.log('[V2CaptionsSidebar] Already creating captions, ignoring click');
            return;
        }

        try {
            const composition = CompositionService.composition;
            if (!composition) return;

            const scriptClip: any = CompositionService.getScriptLayerClip?.();
            if (!scriptClip) {
                alert("Add a script clip first to use captions.");
                return;
            }

            const { clipStart, clipStop, scriptRange } = resolveCaptionsTarget(scriptClip, applyScope);

            if (!opts?.skipExistingCheck) {
                const existingClips = findOverlappingCaptionsClips(composition, scriptClip, clipStart, clipStop);
                if (existingClips.length > 0) {
                    setPendingPresetAction({ preset, existingClips, scope: applyScope });
                    return;
                }
            }

            const projectId = String(ProjectAssetsStore.projectId ?? (window as any).__librettoProjectId ?? "");
            if (!projectId) {
                alert("Missing project id for transcript lookup.");
                return;
            }

            const captionScale = getCaptionScaleForComposition(composition);
            const scaledTextStyle = scaleCaptionTextStyle(preset.textStyle, captionScale);
            const scaledHighlightStyle = scaleCaptionHighlightStyle(preset.highlightStyle, captionScale);
            const scaledBackground = scaleCaptionBackground(preset.background, captionScale);

            setIsCreating(true);

            // Create the CaptionsClip (hydrate words asynchronously after insertion)
            const captionsClip = new CaptionsClip({
                scriptClipId: scriptClip.id,
                timingScope: applyScope,
                displayMode: preset.displayMode,
                textCase: preset.textCase,
                textAlign: preset.textAlign,
                maxWidth: preset.maxWidth,
                textStyle: scaledTextStyle as any,
                highlightStyle: scaledHighlightStyle,
                background: scaledBackground,
                phraseConfig: preset.phraseConfig,
                position: { x: '50%', y: '85%' }, // Position near bottom of screen
                start: clipStart,
                stop: clipStop,
                words: [],
            });

            // Add to composition
            await composition.add(captionsClip);

            console.log('[V2CaptionsSidebar] CaptionsClip added successfully:', captionsClip.id);

            // Hydrate transcript words (script-clip local domain). Do not persist words to Firestore.
            const audioEntries: any[] = Array.isArray(scriptClip.getAudioEntries?.()) ? scriptClip.getAudioEntries() : [];
            const captionWords: CaptionWord[] = [];

            const MIN_ENERGY_RATIO = 0.2;

            for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
                const entry: any = audioEntries[sourceIndex];
                const assetId = entry?.assetId ? String(entry.assetId) : "";
                if (!assetId) continue;

                const trackRecord: any =
                    ProjectAssetsStore.projectTracks?.find((t: any) => t.id === assetId || t.trackId === assetId) ?? null;
                const transcriptId = trackRecord?.activeTranscriptId;
                if (!transcriptId) continue;

                let transcriptJson: any | undefined;
                try {
                    transcriptJson = await GetTranscript({ transcriptId, projectId });
                } catch {
                    transcriptJson = undefined;
                }
                if (!transcriptJson?.words || !Array.isArray(transcriptJson.words)) continue;

                // Support multi-segment placement (sequential sources)
                const entrySegments: Array<{ offsetSec: number; playbackRate: number; rangeInSec: number; rangeOutSec: number }> = [];
                if (Array.isArray(entry?.segments) && entry.segments.length > 0) {
                    for (const seg of entry.segments) {
                        const offsetSec = Number(seg?.offsetSec ?? 0);
                        const playbackRate = Number(seg?.playbackRate ?? entry?.playbackRate ?? 1.0) || 1.0;
                        const rangeInSec = Number(seg?.range?.inSec ?? 0);
                        const rangeOutSec = Number(seg?.range?.outSec ?? 0);
                        if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) continue;
                        if (!Number.isFinite(rangeInSec) || !Number.isFinite(rangeOutSec) || rangeOutSec <= rangeInSec) continue;
                        entrySegments.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
                    }
                } else {
                    const range = entry?.range;
                    const offsetSec = Number(entry?.offsetSec ?? 0) || 0;
                    const playbackRate = Number(entry?.playbackRate ?? 1.0) || 1.0;
                    const sourceDuration = Number(trackRecord?.duration ?? 0);
                    const rangeInSec = Number(range?.inSec ?? 0);
                    const rangeOutSec = Number(range?.outSec ?? (sourceDuration > 0 ? sourceDuration : Infinity));
                    entrySegments.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
                }

                for (const seg of entrySegments) {
                    for (const w of transcriptJson.words) {
                        if ((w.type ?? "word") !== "word") continue;
                        if (w.energy?.ratio != null && w.energy.ratio > 0 && w.energy.ratio < MIN_ENERGY_RATIO) continue;

                        const wordStartSrc = Number(w.t0 ?? w.start ?? 0);
                        const wordEndSrc = Number(w.t1 ?? w.end ?? wordStartSrc);
                        if (!Number.isFinite(wordStartSrc) || !Number.isFinite(wordEndSrc)) continue;
                        if (wordStartSrc < seg.rangeInSec || wordEndSrc > seg.rangeOutSec) continue;

                        const startSec = (wordStartSrc - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
                        const endSec = (wordEndSrc - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
                        if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) continue;

                        if (scriptRange) {
                            if (startSec < scriptRange.startSec || endSec > scriptRange.endSec) continue;
                        }

                        // Transcript-only edits: apply word corrections + hidden ranges to captions/export.
                        if (ScriptLayerStore.isTimeInTranscriptHiddenRange(startSec)) continue;
                        const correctedText =
                            ScriptLayerStore.getWordCorrectionText({
                                sourceIndex,
                                srcStartSec: wordStartSrc,
                                srcEndSec: wordEndSrc,
                            }) ?? String(w.text ?? "").trim();

                        captionWords.push({
                            text: correctedText,
                            startSec,
                            endSec,
                            speaker: undefined,
                            srcStartSec: wordStartSrc,
                            srcEndSec: wordEndSrc,
                            sourceIndex,
                        });
                    }
                }
            }

            captionWords.sort((a, b) => a.startSec - b.startSec);
            (captionsClip as any).words = captionWords;
            try { CompositionService.recomputeFrame(); } catch {}
            try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
            try { CompositionService.notifyClipUpdated(captionsClip as any); } catch {}

            // Call the optional callback
            onSelectPreset?.(preset);

        } catch (error) {
            console.error('[V2CaptionsSidebar] Failed to create captions clip:', error);
        } finally {
            setIsCreating(false);
        }
    };

    const handlePresetClick = async (preset: CaptionPresetConfig) => {
        if (pendingPresetAction) return;
        await createCaptionsClip(preset);
    };

    const closePendingPresetDialog = () => {
        setPendingPresetAction(null);
    };

    const handleAddAdditionalCaptions = async () => {
        const pending = pendingPresetAction;
        if (!pending) return;
        setPendingPresetAction(null);
        await createCaptionsClip(pending.preset, { skipExistingCheck: true });
    };

    const handleReplaceExistingCaptions = () => {
        const pending = pendingPresetAction;
        if (!pending) return;

        const composition = CompositionService.composition;
        for (const captionsClip of pending.existingClips) {
            applyPresetToCaptionsClip(captionsClip, pending.preset, composition);
            try { CompositionService.notifyClipUpdated(captionsClip as any); } catch {}
        }
        try { CompositionService.recomputeFrame(); } catch {}
        try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}

        onSelectPreset?.(pending.preset);
        setPendingPresetAction(null);
    };

    const pendingClipCount = pendingPresetAction?.existingClips.length ?? 0;
    const pendingClipPlural = pendingClipCount !== 1;
    const pendingDialogTitle =
        pendingPresetAction?.scope === "scene"
            ? "Captions already cover this scene"
            : "Captions already exist in this composition";
    const pendingDialogMessage = pendingClipPlural
        ? "Replace the existing captions with this preset, keeping their timing, or add another captions layer."
        : "Replace the existing captions with this preset, keeping its timing, or add another captions layer.";

    return (
        <>
            <Box
                data-tour="captions-panel"
                sx={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    p: "3px",
                }}
            >
                {/* Header */}
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", marginLeft: "8px" }}>
                    <Box sx={{ width: "24px", height: "24px", display: "grid", placeItems: "center" }}>
                        <ClosedCaptionOutlinedIcon sx={{ fontSize: 18, color: "var(--app-icon-primary)" }} />
                    </Box>
                    <Box sx={titleTextStyle}>Captions</Box>
                </Box>

                {/* Main panel */}
                <PaperPanel
                    title="Captions"
                    fill
                    headerContent={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'var(--app-text-muted)' }}>
                                Apply to
                            </Typography>
                            <ScopeToggle value={applyScope} onChange={setApplyScope} />
                        </Box>
                    }
                >
                    {/* Preset cards grid */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            pb: 2,
                            opacity: isCreating ? 0.6 : 1,
                            pointerEvents: isCreating ? 'none' : 'auto',
                        }}
                    >
                        {CAPTION_PRESETS.map((preset) => (
                            <PresetCard
                                key={preset.id}
                                preset={preset}
                                onClick={handlePresetClick}
                            />
                        ))}
                    </Box>
                </PaperPanel>
            </Box>

            <Dialog
                open={Boolean(pendingPresetAction)}
                onClose={closePendingPresetDialog}
                PaperProps={{
                    sx: {
                        width: "430px",
                        padding: "20px 24px",
                        borderRadius: "12px",
                        boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.08)",
                    },
                }}
            >
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "15px",
                        fontWeight: 500,
                        lineHeight: "22px",
                        color: "#1A1A1A",
                        mb: 0.5,
                    }}
                >
                    {pendingDialogTitle}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 400,
                        color: "#6B7280",
                        lineHeight: "18px",
                        mb: 2.5,
                    }}
                >
                    {pendingDialogMessage}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <Button
                        onClick={handleAddAdditionalCaptions}
                        variant="outlined"
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "13px",
                            textTransform: "none",
                            borderColor: "#D1D5DB",
                            color: "#374151",
                            "&:hover": { borderColor: "#9CA3AF", backgroundColor: "#F9FAFB" },
                        }}
                    >
                        Add another layer
                    </Button>
                    <Button
                        onClick={handleReplaceExistingCaptions}
                        variant="contained"
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "13px",
                            textTransform: "none",
                            backgroundColor: "#1A1A1A",
                            "&:hover": { backgroundColor: "#111827" },
                        }}
                    >
                        Replace with this preset
                    </Button>
                </Box>
            </Dialog>
        </>
    );
};

export default V2CaptionsSidebar;
